/**
 * Application roles for authorization.
 * These must match the app roles configured in Microsoft Entra (Azure AD).
 *
 * Roles are assigned to users in the Entra app registration and appear
 * in the 'roles' claim of the ID token.
 */

/**
 * Full administrative access to all admin portal features.
 * Can manage templates, event types, view all audit events, and access all admin functions.
 */
export const SUPER_ADMIN = 'SuperAdmin';

/**
 * Can manage system templates and event types.
 * Cannot view user data, audit logs, or perform other administrative functions.
 * Intended for content management staff.
 */
export const CONTENT_MANAGER = 'ContentManager';

/**
 * Read-only access to audit events and user information.
 * For customer support agents who need to investigate issues.
 * Cannot modify templates or event types.
 */
export const SUPPORT_AGENT = 'SupportAgent';

/**
 * All admin roles that grant access to the admin portal.
 */
export const ALL_ADMIN_ROLES = [SUPER_ADMIN, CONTENT_MANAGER, SUPPORT_AGENT] as const;

/**
 * Roles that can manage content (templates and event types).
 */
export const CONTENT_MANAGEMENT_ROLES = [SUPER_ADMIN, CONTENT_MANAGER] as const;

/**
 * Roles that can view audit events and user support information.
 */
export const SUPPORT_ROLES = [SUPER_ADMIN, SUPPORT_AGENT] as const;

/**
 * Type for all admin roles
 */
export type AdminRole = typeof ALL_ADMIN_ROLES[number];

/**
 * Check if a role is an admin role
 */
export const isAdminRole = (role: string): role is AdminRole => {
  return ALL_ADMIN_ROLES.includes(role as AdminRole);
};

/**
 * Check if roles include any admin role
 */
export const hasAnyAdminRole = (roles: string[]): boolean => {
  return roles.some(r => isAdminRole(r));
};

/**
 * Check if roles include content management access
 */
export const hasContentManagementAccess = (roles: string[]): boolean => {
  return roles.some(r => CONTENT_MANAGEMENT_ROLES.includes(r as typeof CONTENT_MANAGEMENT_ROLES[number]));
};

/**
 * Check if roles include support access
 */
export const hasSupportAccess = (roles: string[]): boolean => {
  return roles.some(r => SUPPORT_ROLES.includes(r as typeof SUPPORT_ROLES[number]));
};
